import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";

const ONE_ON_ONE_FAQS = [
  {
    q: "What is one-on-one coaching?",
    a: "It's a private, unhurried container where we sit with what's actually going on beneath the surface of your career and life. We slow down enough to hear the story you've been carrying, gently question it, and refine the next honest move. Warm. Confidential. Yours.",
  },
  {
    q: "What can I expect?",
    a: "Very practical, everyday change &mdash; not abstract transformation. Feeling calmer at home. Thinking clearly at work. Getting your energy and patience back. We meet fortnightly on video across a structured 6-week or 8-week programme, with small reflection prompts between sessions (breadcrumbs, not homework). You leave sessions lighter, not more full of things to do.",
  },
  {
    q: "Who is this for?",
    a: "Working men dealing with stress, emotional shutdown, work-life imbalance, loss of purpose, or strain in their relationships. If you look successful on paper but feel stuck, switched-off, or quietly running on empty, you're in the right place. Partners sometimes reach out first &mdash; that's welcome too.",
  },
  {
    q: "How do we start?",
    a: "With a free discovery call. No pitch, no pressure. Just a conversation to see if we're a fit and whether now is the right season for this work.",
  },
];

const CORPORATE_FAQS = [
  {
    q: "What are corporate wellness sessions?",
    a: "Grounded, human sessions I bring into your organisation &mdash; keynotes, workshops and small-group circles &mdash; on burnout, clarity, communication and leading from a steadier inner place. Warm-hearted, evidence-informed, and never corporate-cliche. Includes a named offering &mdash; <strong>Men&rsquo;s Wellbeing at Work</strong> &mdash; designed for organisations that want to actively support the emotional health of the men on their teams.",
  },
  {
    q: "What can our team expect?",
    a: "Real conversation, not slide-deck theatre. Space for people to be honest about how they're actually doing, tools they can use the same afternoon, and a felt sense of care that keeps rippling long after the session ends.",
  },
  {
    q: "Who is this for?",
    a: "Leadership teams, employee resource groups, men&rsquo;s networks, and whole organisations navigating change, burnout culture, or a season where people need to feel seen again. We tailor every session to your context.",
  },
  {
    q: "How do we bring you in?",
    a: "Share a little about your team, the moment you're in, and what you're hoping for. I'll come back with a proposal shaped around that &mdash; a single session, a series, or an ongoing partnership.",
  },
];

import { PRIYA } from "@/lib/images";

const IMG_ONE_ON_ONE = PRIYA.heroPortrait;
const IMG_CORPORATE = PRIYA.corporate;

function FaqCard({ item, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border overflow-hidden transition-all"
      style={{ background: "#FAF6EC", borderColor: "rgba(27,67,50,0.14)" }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-6 text-left p-6 md:p-7"
        data-testid={`${testid}-toggle`}
      >
        <span className="font-serif text-xl md:text-2xl" style={{ color: "#1B4332" }}>{item.q}</span>
        <span
          className="flex-none mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full border transition-transform"
          style={{
            borderColor: "#C9A84C",
            color: "#1B4332",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ChevronDown size={16} />
        </span>
      </button>
      {open && (
        <div className="px-6 md:px-7 pb-7 -mt-1 text-[15px] leading-relaxed"
             style={{ color: "#2A2621" }}
             data-testid={`${testid}-answer`}>
          {item.a}
        </div>
      )}
    </div>
  );
}

function ServiceBlock({ id, eyebrow, title, tagline, blurb, image, imageAlt, faqs, faqPrefix }) {
  return (
    <section id={id} data-testid={`service-${id}`} className="container-refined py-20 md:py-28">
      <div className="grid gap-12 md:grid-cols-[1fr_1.1fr] items-start">
        <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border md:sticky md:top-28"
             style={{ borderColor: "rgba(201,168,76,0.5)", boxShadow: "0 40px 80px -40px rgba(27,67,50,0.4)" }}>
          <img src={image} alt={imageAlt} className="h-full w-full object-cover" />
        </div>

        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="font-serif mt-3 text-3xl sm:text-4xl md:text-5xl leading-tight">{title}</h2>
          <p className="font-script text-3xl mt-2">{tagline}</p>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>{blurb}</p>

          <div className="mt-8 grid gap-4">
            {faqs.map((f, i) => (
              <FaqCard key={f.q} item={f} testid={`${faqPrefix}-faq-${i}`} />
            ))}
          </div>

          <Link to="/contact" data-testid={`${faqPrefix}-cta`} className="btn-gold mt-10">
            Start a conversation <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="container-refined pt-16 md:pt-24 pb-6">
        <div className="max-w-3xl animate-fade-up">
          <span className="eyebrow">Ways we can work together</span>
          <h1 className="font-serif mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Two doors into the same room &mdash; the one where you get to be honest.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            Whether you&rsquo;re here for yourself or bringing this into your organisation, the
            posture is the same: warm, unhurried, and rooted in the belief that people don&rsquo;t
            need fixing &mdash; they need hearing.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#one-on-one" data-testid="services-anchor-1to1" className="btn-outline-forest">1:1 Coaching</a>
            <a href="#corporate" data-testid="services-anchor-corp" className="btn-outline-forest">Corporate Events</a>
          </div>
        </div>
      </section>

      <ServiceBlock
        id="one-on-one"
        eyebrow="1:1 Coaching"
        title="Private coaching for working men who look fine, and quietly aren&rsquo;t."
        tagline="From switched-off to steady."
        blurb="A structured coaching partnership over a 6-week or 8-week programme, held in complete confidence. We work with what's actually happening in your career, your relationships, and the quieter interior places most of your life hasn't had room for. You leave calmer at home, clearer at work, and more like yourself again."
        image={IMG_ONE_ON_ONE}
        imageAlt="Priya, seated in a book-lined study, ready to listen"
        faqs={ONE_ON_ONE_FAQS}
        faqPrefix="one"
      />

      <div className="container-refined">
        <div className="divider-leaf w-full justify-center">
          <span className="font-script text-3xl">&amp;</span>
        </div>
      </div>

      <ServiceBlock
        id="corporate"
        eyebrow="Corporate Events"
        title="Warm-hearted wellness for teams &mdash; including Men&rsquo;s Wellbeing at Work."
        tagline="Bring the human back in."
        blurb="Keynotes, workshops and small circles designed for the reality of modern corporate life &mdash; burnout, hidden overwhelm, communication under pressure, and leading with clarity. Includes a dedicated Men&rsquo;s Wellbeing at Work stream for organisations that want to support the emotional health of the men on their teams. Shaped around your team, delivered with care, and never delivered from a script."
        image={IMG_CORPORATE}
        imageAlt="Priya in a corporate space, bringing warmth into the workplace"
        faqs={CORPORATE_FAQS}
        faqPrefix="corp"
      />

      {/* Closing CTA */}
      <section className="container-refined pb-24">
        <div className="rounded-3xl p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between"
             style={{ background: "#1B4332", color: "#FAF6EC" }}>
          <div>
            <span className="eyebrow" style={{ color: "#C9A84C" }}>Not sure which fits?</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3 max-w-xl">
              Tell me what&rsquo;s going on. We&rsquo;ll figure out the right door together.
            </h3>
          </div>
          <Link to="/contact" data-testid="services-cta-book" className="btn-gold">
            Reach out <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
